import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle2, XCircle, AlertCircle, ChevronRight, Loader2,
  Target, ListChecks, Zap, Workflow, Rocket, RefreshCw, Info,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useCadence } from '@/contexts/CadenceContext'
import { useCadenceFlowMetrics, type FlowStep } from '@/hooks/useCadenceFlowMetrics'
import { useOutreachEngineStatus } from '@/hooks/useOutreachEngineStatus'
import { stepGeneratesMessage, stepNeedsRubric } from '@/lib/cadence-step-predicates'

// FlowStep now carries the per-step `completeness` block (shared hook type). This
// section reuses the SAME rubric/angle coverage the edge fn computes, keyed by
// (step_type, day_offset) exactly like runtime chief-supervise-message.
//
// The ANGLE check uses stepGeneratesMessage (comments DO get an angle). The
// RUBRIC check uses stepNeedsRubric (comments do NOT — Carlos scores them with
// structural rules, so demanding a rubric would raise an irresoluble warning).
// Both predicates come from @/lib/cadence-step-predicates.

// 'inherited' = the check resolves, but ONLY because it falls back to the
// org-global doctrine (merchant-Yuno, the 9-day) rather than owning its own row.
// It is functional (not a warn/fail) yet NOT full green — a fresh segment cadence
// (e.g. 'Banca') would otherwise read "N/N en verde" while nothing is actually
// tailored to it. Rendered blue/info + a 'heredado' sub-badge, and it degrades the
// header out of full green. Never counts toward okCount.
type CheckState = 'ok' | 'inherited' | 'warn' | 'fail' | 'loading'

interface ChecklistItem {
  key: string
  label: string
  detail: string
  state: CheckState
  tab?: 'steps' | 'policy' | 'angles' | 'limits'
  cta?: string
  // When set, the CTA button runs this action instead of navigating to `tab`.
  // Used for: the "Reintentar" that refetches the metrics query (mirrors the
  // isError handling in StepsSection.tsx:214-222), and the "Abrir builder" that
  // navigates to the real CadenceBuilder route (/cadences/:id).
  onCta?: () => void
  // Distinguishes the CTA icon for onCta actions: 'refetch' → RefreshCw (retry),
  // 'navigate' → Rocket + ChevronRight (opens the builder). Defaults to 'refetch'
  // when omitted so the existing Reintentar CTA keeps its icon.
  onCtaKind?: 'refetch' | 'navigate'
}

function StateIcon({ state }: { state: CheckState }) {
  if (state === 'ok') return <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
  if (state === 'inherited') return <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0" />
  if (state === 'loading') return <Loader2 className="h-5 w-5 text-muted-foreground animate-spin shrink-0" />
  if (state === 'warn') return <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
  return <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" />
}

// ────────────────────────────────────────────────────────────────────────────
// SummarySection — the "Resumen" tab. An actionable completeness checklist for
// THIS cadence + a short business rationale. Reads the cadence's steps (from the
// context) and the per-step rubric / angle coverage (scoped to this cadence with
// org-global fallback) to tell the owner exactly what is missing and where to fix it.
// ────────────────────────────────────────────────────────────────────────────
export function SummarySection({
  cadenceId,
  onGoTab,
}: {
  cadenceId: string
  onGoTab: (tab: 'steps' | 'policy' | 'angles' | 'limits') => void
}) {
  const { cadences } = useCadence()
  const navigate = useNavigate()

  // Honest engine state (CA-003). `engineOff` is true ONLY when we have data AND
  // it says the outreach engine is off — never true while loading/unknown. Used
  // to degrade the is-active item so "activa" is not misread as "está enviando",
  // cruzando la MISMA señal que el badge del header (AutomCadence.tsx:202-221).
  const { engineOff } = useOutreachEngineStatus()

  const cadence = cadences.find((c) => c.id === cadenceId) ?? null
  const steps = useMemo(
    () => [...(cadence?.steps ?? [])].sort((a, b) => {
      if (a.day_offset !== b.day_offset) return a.day_offset - b.day_offset
      return a.order_in_day - b.order_in_day
    }),
    [cadence],
  )
  const messageSteps = useMemo(
    () => steps.filter((s) => stepGeneratesMessage(s)),
    [steps],
  )

  // Social-only cadence: HAS steps, but NONE of them generate an AI message
  // (only plain linkedin_connect / whatsapp / cold_call / tasks). The content
  // checklist (prompt/rubric/angle items) does not render for these, so a
  // near-green header would misread as "lista para producir copy". We surface an
  // explicit amber banner and degrade the header out of green (see below).
  const socialOnly = steps.length > 0 && messageSteps.length === 0

  // flow-metrics StepOut does not carry config_json, so mirror the runtime
  // predicate by reading config_json.send_note from the cadence context, keyed
  // by step id. Used to decide which flow steps count as message-generating.
  const configByStepId = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>()
    for (const s of steps) map.set(s.id, s.config_json ?? {})
    return map
  }, [steps])

  // Coverage is read from cadence-flow-metrics — the SAME edge fn that computes
  // per-step `completeness.tiene_rubrica` / `tiene_angle` keyed by (step_type,
  // day_offset), i.e. the runtime cascade (cadence-scoped > org-global NULL). This
  // is the single source of truth: keying by day_offset ALONE would false-green a
  // day where one step type has a rubric but another (same day) doesn't.
  const metricsQuery = useCadenceFlowMetrics(cadenceId)
  const {
    isLoading: metricsLoading,
    isError: metricsError,
    refetch: refetchMetrics,
  } = metricsQuery
  const flowSteps = useMemo<FlowStep[]>(
    () => metricsQuery.data?.steps ?? [],
    [metricsQuery.data],
  )

  // Message steps as seen by flow-metrics (per (step_type, day_offset)), so the
  // rubric/angle checks below key exactly like runtime — not by day alone. Uses
  // the same runtime predicate: a plain-invite linkedin_connect (send_note !==
  // true, read from context by step id) is excluded.
  const flowMessageSteps = useMemo(
    () => flowSteps.filter((s) => stepGeneratesMessage({
      step_type: s.step_type,
      config_json: configByStepId.get(s.step_id),
    })),
    [flowSteps, configByStepId],
  )

  // Steps that NEED a Carlos rubric (message steps minus linkedin_comment, plus
  // the Day-9 task). The rubric coverage check uses THIS list, not
  // flowMessageSteps — a linkedin_comment is a message step but Carlos scores it
  // with structural rules, so demanding a rubric would raise a permanently
  // irresoluble "Sin rúbrica" warning. Context-side twin used to gate the item.
  const rubricSteps = useMemo(
    () => steps.filter((s) => stepNeedsRubric(s)),
    [steps],
  )
  const flowRubricSteps = useMemo(
    () => flowSteps.filter((s) => stepNeedsRubric({
      step_type: s.step_type,
      config_json: configByStepId.get(s.step_id),
    })),
    [flowSteps, configByStepId],
  )

  const items: ChecklistItem[] = useMemo(() => {
    const list: ChecklistItem[] = []

    // 1) Has steps?
    list.push({
      key: 'has-steps',
      label: '¿Tiene pasos configurados?',
      detail: steps.length > 0
        ? `${steps.length} paso${steps.length === 1 ? '' : 's'} en total (${messageSteps.length} de mensaje).`
        : 'Esta cadencia no tiene ningún paso. Agrégalos en el builder.',
      state: steps.length > 0 ? 'ok' : 'fail',
      tab: 'steps',
      cta: 'Ir a Steps',
    })

    // 2) Each message step has a prompt (config_json.ai_prompt_id)?
    const stepsWithoutPrompt = messageSteps.filter((s) => {
      const cfg = s.config_json as Record<string, unknown> | null
      return !cfg?.ai_prompt_id
    })
    if (messageSteps.length > 0) {
      list.push({
        key: 'has-prompts',
        label: '¿Cada paso de mensaje tiene prompt?',
        detail: stepsWithoutPrompt.length === 0
          ? 'Todos los pasos de mensaje tienen un prompt asignado.'
          : `${stepsWithoutPrompt.length} paso${stepsWithoutPrompt.length === 1 ? '' : 's'} de mensaje sin prompt — usarán el fallback del motor.`,
        state: stepsWithoutPrompt.length === 0 ? 'ok' : 'warn',
        tab: 'steps',
        cta: 'Revisar en Steps',
      })
    }

    // Label helper for the per-(step,day) misses shown to the user.
    const labelStep = (s: FlowStep) => `${s.step_type} · Día ${s.day_offset}`

    // The rubric/angle checks read cadence-flow-metrics. When it ERRORS, `data`
    // is undefined → flow*Steps=[] and we CANNOT silently drop these items (that
    // would false-green the header): render them as an explicit 'warn' with a
    // Reintentar CTA (mirrors StepsSection.tsx:214-222). We gate each on its
    // context-side twin (rubricSteps / messageSteps, always available) so the
    // item appears even though the flow list is empty in the error case.

    // 3) Rubric per RUBRIC step (step_type::day_offset), from completeness. Uses
    // flowRubricSteps — a linkedin_comment is a message step but needs NO rubric,
    // so it must not raise a "Sin rúbrica" warning. A cadence with only comment
    // steps has no rubric steps → this item does not render. While metrics load,
    // render a NEUTRAL 'loading' state (gray spinner) — not amber warn — excluded
    // from the "/N en verde" counter so the count does not flicker. On ERROR
    // render an amber 'warn' with a Reintentar CTA — never leave it out.
    if (flowRubricSteps.length > 0 || (rubricSteps.length > 0 && (metricsLoading || metricsError))) {
      const missingRubric = flowRubricSteps.filter((s) => s.completeness?.tiene_rubrica !== true)
      // Provenance: among the steps that DO resolve to a rubric, is EVERY one
      // inherited from the org-global doctrine (cadence_id IS NULL)? If so, the
      // check is functional but not "own" → 'inherited' (blue/info), never full
      // green. A single own rubric flips it to full green. `rubrica_heredada` is
      // optional in the payload, so treat absent as own (=== true only).
      const presentRubric = flowRubricSteps.filter((s) => s.completeness?.tiene_rubrica === true)
      const allRubricInherited = presentRubric.length > 0
        && presentRubric.every((s) => s.completeness?.rubrica_heredada === true)
      const rubricState: CheckState = metricsError
        ? 'warn'
        : metricsLoading
          ? 'loading'
          : missingRubric.length > 0
            ? 'warn'
            : allRubricInherited
              ? 'inherited'
              : 'ok'
      list.push({
        key: 'has-rubrics',
        label: rubricState === 'inherited'
          ? '¿Cada touch tiene rúbrica de Carlos? · heredada (org-global)'
          : '¿Cada touch tiene rúbrica de Carlos?',
        detail: metricsError
          ? 'No se pudo comprobar la cobertura — reintenta.'
          : metricsLoading
            ? 'Comprobando…'
            : missingRubric.length > 0
              ? `Pasos sin rúbrica efectiva: ${missingRubric.map(labelStep).join(', ')} — Carlos caerá al umbral global.`
              : allRubricInherited
                ? 'Resuelve, pero usa la política org-global compartida (la de la 9-day) — revisa que aplique a este segmento (edítala en Política para hacerla propia).'
                : 'Todos los pasos con rúbrica resuelven a una propia de esta cadencia.',
        state: rubricState,
        ...(metricsError
          ? { cta: 'Reintentar', onCta: () => refetchMetrics() }
          : { tab: 'policy', cta: 'Ir a Política' }),
      })
    }

    // 4) Angle per message STEP, from completeness (angles resolve by day at
    // runtime). Uses flowMessageSteps — comments DO carry an angle.
    if (flowMessageSteps.length > 0 || (messageSteps.length > 0 && (metricsLoading || metricsError))) {
      const missingAngles = flowMessageSteps.filter((s) => s.completeness?.tiene_angle !== true)
      // Same provenance logic as the rubric check: if every resolving angle is
      // org-global (angle_heredado === true), the check is 'inherited' (blue),
      // not full green. Absent flag → treat as own.
      const presentAngles = flowMessageSteps.filter((s) => s.completeness?.tiene_angle === true)
      const allAnglesInherited = presentAngles.length > 0
        && presentAngles.every((s) => s.completeness?.angle_heredado === true)
      const angleState: CheckState = metricsError
        ? 'warn'
        : metricsLoading
          ? 'loading'
          : missingAngles.length > 0
            ? 'warn'
            : allAnglesInherited
              ? 'inherited'
              : 'ok'
      list.push({
        key: 'has-angles',
        label: angleState === 'inherited'
          ? '¿Cada touch tiene ángulo (value-prop)? · heredado (org-global)'
          : '¿Cada touch tiene ángulo (value-prop)?',
        detail: metricsError
          ? 'No se pudo comprobar la cobertura — reintenta.'
          : metricsLoading
            ? 'Comprobando…'
            : missingAngles.length > 0
              ? `Pasos sin ángulo efectivo: ${missingAngles.map(labelStep).join(', ')} — el mensaje puede repetir el pitch.`
              : allAnglesInherited
                ? 'Resuelve, pero usa los ángulos org-global compartidos (los de la 9-day) — revisa que apliquen a este segmento (edítalos en Ángulos para hacerlos propios).'
                : 'Todos los pasos de mensaje resuelven a un ángulo propio de esta cadencia.',
        state: angleState,
        ...(metricsError
          ? { cta: 'Reintentar', onCta: () => refetchMetrics() }
          : { tab: 'angles', cta: 'Ir a Ángulos' }),
      })
    }

    // 5) Is active? The CTA opens the REAL CadenceBuilder (/cadences/:id, App.tsx
    // route gated by section_cadences) — the module's "steps" tab CANNOT activate
    // the cadence (activation lives in the builder), so navigating there is what
    // actually unblocks the user. When cadence.id is missing (row not in context),
    // omit onCta (and no tab) so the CTA button does not render — rather than
    // routing to /cadences/undefined. The render guard is `cta && (onCta || tab)`.
    // When the cadence is active BUT the outreach engine is off, an 'ok' green
    // tick would read as "está enviando" — but nothing sends while the engine is
    // down. Cross the SAME signal the header badge uses (AutomCadence.tsx:202-221)
    // and degrade this item to 'warn' with a visible label suffix + a detail that
    // points to /settings/outreach. `engineOff` is true only when we KNOW the
    // engine is off (never while loading), so an unknown/loading engine stays 'ok'.
    const isActive = cadence?.status === 'active'
    const activeButEngineOff = isActive && engineOff
    list.push({
      key: 'is-active',
      label: activeButEngineOff
        ? '¿La cadencia está activa? · pero el motor está apagado'
        : '¿La cadencia está activa?',
      detail: activeButEngineOff
        ? 'La cadencia está activa, pero el motor de outreach está apagado: los pasos programados NO se envían. Enciéndelo en /settings/outreach.'
        : isActive
          ? 'La cadencia está activa. (El envío real depende además del motor de outreach.)'
          : 'La cadencia está en borrador — actívala en el builder cuando esté lista.',
      state: activeButEngineOff ? 'warn' : isActive ? 'ok' : 'warn',
      cta: 'Abrir builder',
      ...(cadence?.id
        ? { onCta: () => navigate(`/cadences/${cadence.id}`), onCtaKind: 'navigate' as const }
        : {}),
    })

    return list
  }, [steps, messageSteps, rubricSteps, flowMessageSteps, flowRubricSteps, metricsLoading, metricsError, refetchMetrics, cadence, navigate, engineOff])

  // While ANY item is still 'loading' the counter is transitory: e.g. it can read
  // "3/3 en verde" (with a green tick) before the rubric/angle checks finish and
  // add more items. Gate on this so we show "Comprobando…" (and suppress the green
  // header tick) until nothing is loading — only then is the count definitive.
  const anyLoading = items.some((i) => i.state === 'loading')

  // Exclude items still loading from BOTH numerator and denominator so the
  // "/N en verde" count does not flicker (e.g. 3/5 → 5/5) while metrics resolve.
  const resolvedItems = items.filter((i) => i.state !== 'loading')
  const okCount = resolvedItems.filter((i) => i.state === 'ok').length
  const failCount = items.filter((i) => i.state === 'fail').length
  // Amber items (missing prompt/rubric/angle, draft status) are NOT green — the
  // header check must account for them too, otherwise it shows a green tick while
  // warns remain and the "okCount/resolvedItems.length" counter (which keeps warns
  // in the denominator) reads e.g. 3/5 next to a green header. Loading is already
  // excluded from resolvedItems, so it never lands in warnCount.
  const warnCount = resolvedItems.filter((i) => i.state === 'warn').length
  // 'inherited' checks resolve only via the org-global doctrine (the 9-day). They
  // are functional but NOT own — they must keep the header out of full green and
  // trigger the "toda su política es heredada" banner. Never counted as green.
  const inheritedCount = resolvedItems.filter((i) => i.state === 'inherited').length
  // The whole cadence's content policy is inherited when there is at least one
  // inherited content check AND no content check owns its own row (no 'ok' content
  // item). We scope this to the rubric/angle items so the is-active / has-steps
  // items don't mask it. Only meaningful once metrics have resolved.
  const contentItems = resolvedItems.filter((i) => i.key === 'has-rubrics' || i.key === 'has-angles')
  const allContentInherited = !anyLoading && !metricsError
    && contentItems.length > 0
    && contentItems.every((i) => i.state === 'inherited')

  const tabIcon = (tab?: ChecklistItem['tab']) => {
    switch (tab) {
      case 'policy': return <ListChecks className="h-3.5 w-3.5" />
      case 'angles': return <Target className="h-3.5 w-3.5" />
      case 'limits': return <Zap className="h-3.5 w-3.5" />
      default: return <Workflow className="h-3.5 w-3.5" />
    }
  }

  return (
    <div className="space-y-6">
      {/* Business rationale */}
      <Card className="border-primary/20 bg-primary/[0.03]">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Rocket className="h-5 w-5 text-primary" />
            Para qué sirve esta cadencia
          </CardTitle>
          <CardDescription className="max-w-2xl">
            Cada cadencia es un pipeline de outreach automatizado para traer leads enterprise
            calificados: los pasos definen QUÉ se envía y CUÁNDO, el prompt de cada paso define
            CÓMO se redacta, el ángulo mantiene cada touch en una propuesta de valor distinta, la
            rúbrica de Carlos decide si el mensaje sale, y los límites protegen presupuesto y
            calidad. Completa el checklist de abajo para que esta cadencia esté lista para producir.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Social-only warning: the cadence has steps but none generate an AI
          message, so it will not produce automated copy. Prominent amber banner
          ABOVE the checklist, and the header below is degraded out of green. */}
      {socialOnly && (
        <Card className="border-amber-500/40 bg-amber-500/[0.06]">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                Esta cadencia no tiene pasos que generen mensajes con IA
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Solo invitaciones en frío / WhatsApp / tareas. No producirá copy
                automatizado; el checklist de contenido no aplica.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fully-inherited warning: the cadence's whole content policy resolves only
          via the org-global doctrine (the 9-day, merchant-Yuno) — nothing is
          tailored to this segment. Blue/info banner ABOVE the checklist; the header
          below is degraded out of full green. Hidden for social-only cadences
          (their content checklist doesn't apply) and while metrics load. */}
      {allContentInherited && !socialOnly && (
        <Card className="border-blue-500/40 bg-blue-500/[0.06]">
          <CardContent className="flex items-start gap-3 py-4">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                Esta cadencia hereda toda su política de la configuración org-global (la de la 9-day)
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Nada de la rúbrica ni de los ángulos es propio de este segmento todavía.
                Revisa que la doctrina aplique a tu segmento antes de producir — edítala en
                Política / Ángulos para hacerla propia.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Completeness checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            {anyLoading || metricsLoading
              // Still resolving: neutral spinner, never a premature green tick.
              ? <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />
              : failCount === 0 && warnCount === 0 && !socialOnly && !metricsError
                ? (inheritedCount > 0
                    // Functional but inherited → info blue, NOT full green.
                    ? <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    : <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />)
                : <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
            Completitud de la cadencia
            <span className="ml-1 text-sm font-normal text-muted-foreground">
              {anyLoading || metricsLoading
                ? 'Comprobando…'
                : `${okCount}/${resolvedItems.length} en verde`}
            </span>
          </CardTitle>
          <CardDescription>
            Qué le falta a ESTA cadencia para estar lista. Cada punto lleva al tab donde se arregla.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {metricsLoading && items.length === 0 ? (
            <div className="py-10 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Analizando la cadencia…
            </div>
          ) : (
            <ul className="space-y-2">
              {items.map((item) => (
                <li
                  key={item.key}
                  className={`flex items-start gap-3 rounded-lg border p-3${
                    item.state === 'inherited'
                      ? ' border-blue-500/30 bg-blue-500/[0.04]'
                      : ''
                  }`}
                >
                  <StateIcon state={item.state} />
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium">
                      {item.label}
                      {item.state === 'inherited' && (
                        <span className="inline-flex items-center rounded-full border border-blue-500/40 bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                          heredado (org-global)
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.detail}</p>
                  </div>
                  {item.cta && (item.onCta || item.tab) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0 h-8 gap-1 text-xs"
                      onClick={() => (item.onCta ? item.onCta() : onGoTab(item.tab!))}
                    >
                      {item.onCta
                        ? (item.onCtaKind === 'navigate'
                          ? <Rocket className="h-3.5 w-3.5" />
                          : <RefreshCw className="h-3.5 w-3.5" />)
                        : tabIcon(item.tab)}
                      {item.cta}
                      {item.onCta && item.onCtaKind !== 'navigate'
                        ? null
                        : <ChevronRight className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
